// Copyright (c) 2021 WiseTime. All rights reserved.

import { TextEllipsis } from "features/shared/styles"
import React, { useRef } from "react"
import styled from "styled-components/macro"
import { t } from "@lingui/macro"
import RemoveButton from "../../shared/RemoveButton"

const KeywordsArea = styled.div<{ isError: boolean, disabled: boolean }>`
  position: relative;
  border: 1px solid ${({ theme, isError }): string => isError ? theme.colors.lightCoral : theme.colors.gainsboro};
  background-color: ${({ theme, disabled }): string => disabled ? "transparent" : theme.colors.white};
  padding: .2rem 1rem;
`

const Input = styled.input`
  color: ${({ theme }): string => theme.colors.dimGray};
  width: 100%;
  height: 30px;
  padding: 0;
  border: none;
  background-color: transparent;
  &:focus { outline: none; }
`

const Keyword = styled.span<{ readOnly?: boolean }>`
  cursor: default;
  display: inline-block;
  vertical-align: top;
  font-size: 1.1rem;
  font-weight: 600;
  max-width: 150px;
  height: 22px;
  line-height: 21px;
  position: relative;
  padding: 0 .6rem;
  border-radius: 2px;
  color: ${({ theme }): string => theme.colors.dimGray};
  border: 1px solid ${({ theme }): string => theme.colors.silver};
  background-color: ${({ theme }): string => theme.colors.white};
  margin: 5px 5px 0 0;

  &:hover {
    border-style: dashed;
    
    ${RemoveButton} {
      display: inline-block;
    }
  }
`

export const KeywordText = styled.span`
  ${TextEllipsis};
  display: block;
`

type Props = {
  id: string,
  keyword: string,
  keywords: string[],
  isErrored: boolean,
  disabled?: boolean,
  onChangeKeyword: (keyword: string) => void,
  onAddKeyword: (keyword: string) => void,
  onRemoveKeyword: (keyword: string) => void,
}

const EditTagDialogKeywords = ({
  id,
  keyword,
  keywords,
  isErrored,
  disabled,
  onChangeKeyword,
  onAddKeyword,
  onRemoveKeyword,
}: Props): React.ReactElement<Props> => {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      const trimmedKeyword = keyword.trim()
      if (trimmedKeyword.length !== 0 && !isErrored) {
        onAddKeyword(trimmedKeyword)
        onChangeKeyword("")
      }
      e.preventDefault()
    }
  }

  // If the field is blurred but there is a value, add it as a keyword
  const handleBlur = (): void => {
    const trimmedKeyword = keyword.trim()
    if (trimmedKeyword.length !== 0 && !isErrored) {
      onAddKeyword(trimmedKeyword)
      onChangeKeyword("")
    }
  }

  return <>
    <KeywordsArea
      isError={isErrored}
      disabled={disabled === true}
      onClick={(): void => inputRef.current?.focus()}
    >
      <div>
        {keywords.map(keyword => (
          <Keyword
            key={keyword}
            title={keyword}
            onClick={e => e.stopPropagation()}
          >
            <KeywordText>{keyword}</KeywordText>
            <RemoveButton onClick={(): void => onRemoveKeyword(keyword)}/>
          </Keyword>
        ))}
      </div>
      <Input
        id={id}
        ref={inputRef}
        value={keyword}
        disabled={disabled}
        placeholder={t({
          id: "msg_edit_tag_dialog_keyword_placeholder",
          message: "Input a keyword and press Enter"
        })}
        onChange={(event): void => onChangeKeyword(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </KeywordsArea>
  </>
}

export default EditTagDialogKeywords

